import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Sparkles, AlertCircle, ArrowRight } from 'lucide-react';
import { useScreenInit } from '../useScreenInit.js';
const COMPANY_SIZES = ['1–10', '11–50', '51–200', '201–1000', '1000+'];
const USE_CASES = ['Marketing & Ad Campaigns', 'Operations & Workflow Automation', 'Data analysis & reporting', 'Sales & CRM', 'Content & Creative Production', 'Engineering & Development', 'Executive / Leadership Assistant', 'Agency/Consultancy & Client Operations', 'eCommerce & Shopify', 'Other'];
export function WatchGigaDemo() {
  useScreenInit();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [size, setSize] = useState('');
  const [selectedUseCase, setSelectedUseCase] = useState('');
  const [workflows, setWorkflows] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Please enter your work email.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      navigate('/demo/book');
    }, 800);
  };
  return <div className="min-h-screen bg-[#1C1510] flex items-center justify-center py-24 px-6 relative overflow-hidden" data-id="element-7473">
      {/* Background accents */}
      <div className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none" data-id="element-7474" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] rounded-full bg-[#F97316]/[0.08] blur-[120px]" data-id="element-7475" />

      <div className="relative z-10 w-full max-w-2xl" data-id="element-7476">
        <AnimatePresence mode="wait" data-id="element-7477">
          <motion.div key="form" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-[#2B211A] border border-white/10 rounded-3xl shadow-2xl overflow-hidden" data-id="element-7478">
            <div className="p-8 md:p-10 border-b border-white/5" data-id="element-7479">
              <h1 className="font-title font-bold text-3xl text-white mb-3" data-id="element-7480">
                Meet Giga, your team's new coworker.
              </h1>
              <p className="text-neutral-400 leading-relaxed" data-id="element-7481">
                Tell us about your company and what you want to see, so we can
                prepare a personalized live demo. Mentioning specific tools
                helps Giga tailor the examples perfectly to your stack.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="p-8 md:p-10 space-y-8" data-id="element-7482">
              {/* Email */}
              <div className="space-y-3" data-id="element-7483">
                <label htmlFor="email" className="block text-sm font-bold text-white" data-id="element-7484">
                  Your work email
                </label>
                <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" className={`w-full bg-black/20 border ${error ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-[#EA580C]'} rounded-xl px-4 py-3.5 text-white placeholder:text-neutral-600 focus:outline-none focus:ring-1 focus:ring-[#EA580C] transition-colors`} data-id="element-7485" />
                {error && <p className="text-red-400 text-sm flex items-center gap-1.5 mt-2" data-id="element-7486">
                    <AlertCircle className="w-4 h-4" data-id="element-7487" />
                    {error}
                  </p>}
              </div>

              {/* Company Size */}
              <div className="space-y-3" data-id="element-7488">
                <label htmlFor="size" className="block text-sm font-bold text-white" data-id="element-7489">
                  How many people work at your company?
                </label>
                <div className="relative" data-id="element-7490">
                  <select id="size" value={size} onChange={e => setSize(e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3.5 text-white appearance-none focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-colors" data-id="element-7491">
                    <option value="" disabled className="bg-[#2B211A] text-neutral-500" data-id="element-7492">
                      Select company size...
                    </option>
                    {COMPANY_SIZES.map(s => <option key={s} value={s} className="bg-[#2B211A]" data-id="element-7493">
                        {s}
                      </option>)}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-500" data-id="element-7494">
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7495">
                      <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7496" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Use Cases */}
              <div className="space-y-3" data-id="element-7497">
                <label htmlFor="usecase" className="block text-sm font-bold text-white" data-id="element-7498">
                  What would Giga help you with? Select from use cases below:
                </label>
                <div className="relative" data-id="element-7499">
                  <select id="usecase" value={selectedUseCase} onChange={e => setSelectedUseCase(e.target.value)} className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3.5 text-white appearance-none focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-colors" data-id="element-7500">
                    <option value="" disabled className="bg-[#2B211A] text-neutral-500" data-id="element-7501">
                      Select a use case...
                    </option>
                    {USE_CASES.map(uc => <option key={uc} value={uc} className="bg-[#2B211A]" data-id="element-7502">
                        {uc}
                      </option>)}
                  </select>
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-neutral-500" data-id="element-7503">
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-7504">
                      <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-7505" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Specific Workflows */}
              <div className="space-y-3" data-id="element-7506">
                <label htmlFor="workflows" className="block text-sm font-bold text-white" data-id="element-7507">
                  What specific workflows and tools should Giga handle?
                </label>
                <p className="text-xs text-neutral-400 mb-2" data-id="element-7508">
                  List them and we'll prepare live examples for you.
                </p>
                <textarea id="workflows" value={workflows} onChange={e => setWorkflows(e.target.value)} placeholder="e.g. Pulling Stripe revenue into a weekly Slack report, or creating Linear issues from Zendesk tickets..." rows={4} className="w-full bg-black/20 border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#EA580C] focus:ring-1 focus:ring-[#EA580C] transition-colors resize-none" data-id="element-7509" />
              </div>

              {/* Submit */}
              <div className="pt-4 border-t border-white/5 flex justify-end" data-id="element-7510">
                <button type="submit" disabled={isSubmitting} className="bg-[#EA580C] hover:bg-[#C2410C] text-white px-8 py-3.5 rounded-xl font-bold transition-colors shadow-lg shadow-[#EA580C]/20 flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-7511">
                  {isSubmitting ? <span className="flex items-center gap-2" data-id="element-7512">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-7513">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-7514"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-7515"></path>
                      </svg>
                      Preparing...
                    </span> : <>
                      Prepare my live demo
                      <ArrowRight className="w-4 h-4" data-id="element-7516" />
                    </>}
                </button>
              </div>
            </form>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}